import { useCallback, useEffect, useState } from "react";
import type { FC, KeyboardEvent, MouseEvent } from "react";
import styled from "@emotion/styled";
import { useHeadsUpBaseContextPayload } from "@/components/heads-up/analytics";
import {
  MDSButton,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
} from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSModalDialog } from "@/mds/components/overlays/dialog";
import { MDSDialogSlotName } from "@/mds/components/overlays/dialog/dialog.types";
import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import headsUpLiveExplainerGif from "@/assets/images/heads-up/heads-up-live-explainer.gif";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import type { HeadsUpLiveExplainerSource } from "@/domains/metrics";

export interface HeadsUpLiveExplainerAnalytics {
  source: HeadsUpLiveExplainerSource;
  smartCardId: string | null;
  isLiveActive: boolean;
}

interface HeadsUpLiveExplainerProps {
  initiallyOpen?: boolean;
  analytics?: HeadsUpLiveExplainerAnalytics;
}

const MODAL_TITLE = "How Live works";
const MODAL_DESCRIPTION =
  "While you're in a meeting, Mem listens to what's being discussed and will show relevant " +
  "notes from your workspace, so you never miss a connection to something you've already " +
  "captured.";

export const HeadsUpLiveExplainer: FC<HeadsUpLiveExplainerProps> = ({
  initiallyOpen = false,
  analytics,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const basePayload = useHeadsUpBaseContextPayload();

  useEffect(() => {
    setIsOpen(initiallyOpen);
  }, [initiallyOpen]);

  const stopClickPropagation = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const stopHeaderToggleKeyEvent = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  }, []);

  const handleOpen = useCallback(() => {
    if (analytics) {
      trackEvent(TrackedEvent.HeadsUpLiveExplainerOpen, {
        ...basePayload,
        source: analytics.source,
        smart_card_id: analytics.smartCardId,
        is_live_active: analytics.isLiveActive,
      });
    }
    setIsOpen(true);
  }, [analytics, basePayload]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <ButtonEventBoundary onClick={stopClickPropagation} onKeyDown={stopHeaderToggleKeyEvent}>
        <MDSIconButton
          iconKind={MDSIconKind.Help}
          variant={MDSIconButtonVariant.Transparent}
          size={MDSIconButtonSize.XSmall}
          onPress={handleOpen}
          aria-label={MODAL_TITLE}
        />
      </ButtonEventBoundary>

      <MDSModalDialog isOpen={isOpen} handleClose={handleClose} isDismissable={true}>
        <StyledHeader slot={MDSDialogSlotName.Header}>
          <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
            {MODAL_TITLE}
          </MDSText>
        </StyledHeader>

        <StyledContent slot={MDSDialogSlotName.Content}>
          <MDSText variant={MDSTextVariant.ParagraphSm}>{MODAL_DESCRIPTION}</MDSText>
          <StyledImageContainer>
            <StyledImage src={headsUpLiveExplainerGif} alt="Heads Up Live explainer" />
          </StyledImageContainer>
        </StyledContent>

        <StyledFooter slot={MDSDialogSlotName.ButtonsRight}>
          <MDSButton variant={MDSButtonVariant.Contrast} onPress={handleClose}>
            Got it
          </MDSButton>
        </StyledFooter>
      </MDSModalDialog>
    </>
  );
};

const ButtonEventBoundary = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  width: 100%;
`;

const StyledImageContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const StyledFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
